import { ImageScaleView, ViewOpts } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import i18n from 'src/lib/i18n/i18n';
import { STREAK_EVENT_PLAYER_COUNT } from 'src/replicant/streakbattleroyale/streakbattleroyale.ruleset';
import { clamp } from 'src/replicant/utils';
import gsap from 'gsap';
import { getStreakBattleRoyaleGoal } from 'src/replicant/streakbattleroyale/streakbattleroyale.getters';
import { user } from 'src/StateObserver';
import { LangBitmapFontTextShadowView } from 'src/lib/ui/components/LangBitmapFontTextShadowView';
import { streakBattleRoyaleTheme } from '../streakbattleroyale.theme';

type StreakBattleRoyaleInfoPanelType = 'players' | 'level';

type StreakBattleRoyaleInfoSubPanelOpts = {
  type: StreakBattleRoyaleInfoPanelType;
} & ViewOpts;

export default class StreakBattleRoyaleInfoSubPanelV2 extends ImageScaleView {
  private type: StreakBattleRoyaleInfoPanelType;
  private progress!: LangBitmapFontTextView;
  private _value = 0;
  private goal: number;

  public get value() {
    return this._value;
  }

  public set value(v: number) {
    const isPlayers = this.type === 'players';
    this._value = clamp(
      v,
      0,
      isPlayers ? STREAK_EVENT_PLAYER_COUNT : this.goal,
    );
    this.progress.localeText = () =>
      `${this.value}${isPlayers ? `/${STREAK_EVENT_PLAYER_COUNT}` : `/${this.goal}`}`;
  }

  constructor(opts: StreakBattleRoyaleInfoSubPanelOpts) {
    const width = 170;
    const height = 137;

    super({
      ...opts,
      image: streakBattleRoyaleTheme().getAsset('subpanel_info'),
      width,
      height,
      centerOnOrigin: true,
    });

    this.goal = getStreakBattleRoyaleGoal(user());
    this.type = opts.type;
    this.createLabel();
    this.createProgressText();
  }

  private createLabel() {
    new LangBitmapFontTextView({
      superview: this,
      font: 'Body',
      x: this.style.width * 0.5,
      y: 22,
      centerOnOrigin: true,
      localeText: () => i18n(`match2.streakBattleRoyale.popup.${this.type}`),
      align: 'center',
      verticalAlign: 'center',
      color: '#89671C',
      size: 25,
    });
  }

  private createProgressText() {
    const isPlayers = this.type === 'players';

    this.progress = new LangBitmapFontTextShadowView({
      superview: this,
      font: 'Title',
      x: this.style.width * 0.5,
      y: 72,
      centerOnOrigin: true,
      localeText: () =>
        isPlayers
          ? `${STREAK_EVENT_PLAYER_COUNT}/${STREAK_EVENT_PLAYER_COUNT}`
          : `0/${this.goal}`,
      align: 'center',
      verticalAlign: 'center',
      color: '#FFFFFF',
      size: 30,
      textShadowOpacity: 0.25,
      textShadowOffsetY: 4,
    });
  }

  public animateValue(v: number) {
    const diff = Math.abs(this.value - v);
    if (diff === 0) return;
    gsap.to(this, {
      duration: 0.75,
      value: v,
      ease: `steps(${diff})`,
      overwrite: true,
    });
  }
}
